import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTable } from 'primereact/datatable'
import { useHistory } from 'react-router-dom'
import LayoutContainer from 'Components/LayoutComponent'
import { ROUTE_STRINGS, TABLE_CONST } from 'Utils/Constants'
import { Column } from 'primereact/column'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import Loader from 'Components/Loader'
import { Button } from 'primereact/button'
import { DialogModal } from 'Components/UIComponents'
import { commonMethods } from 'Utils/commonMethods'

const ListICOCoffeeReport = ({ setLoading, mockListIcoReport, mockICOReport }) => {
  const { t } = useTranslation()
  const history = useHistory()

  const [icoReport, setICOReport] = useState([])
  const [showTableMessage, setShowTableMessage] = useState(false)
  const [windowSize, setWindowSize] = useState(window.innerWidth)
  const [dialogState, setDialogState] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {}, onDismiss: () => handleDismiss() })

  const paginationButtons = windowSize < TABLE_CONST.maxWidthTab ? TABLE_CONST.mobilePaginationButtonCount : TABLE_CONST.paginationButtonCount

  useEffect(() => {
    fetchICOCoffeeReport()

    window.addEventListener('resize', handleWindowResize)

    // for testing purpose
    if (mockListIcoReport) {
      setICOReport(mockICOReport)
    }

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  const fetchICOCoffeeReport = async () => {
    setLoading(true)
    setShowTableMessage(true)
    setDialogState({ ...dialogState, showModal: false })
    try {
      const icoReportRawData = await apiAdapterCoffeeWeb.getICOCoffeeReport()

      const icoReportData = icoReportRawData.data.returnLst.flatMap((el) => el.months.flatMap((month) => month.data))

      if (icoReportData) {
        const filteredReportData = icoReportData.map((ele) => {
          const localDate = commonMethods.utcToLocalDate(ele.publishedDate)

          return { ...ele, publishedDate: localDate, publishedDateInUTC: ele.publishedDate }
        })

        setICOReport(filteredReportData)
      } else {
        setDialogState({
          ...dialogState,
          showModal: true,
          modalType: 'error',
          message: 'ICO_COFFEE_REPORT_NO_DATA',
          onHide: () => handleDismiss(),
          onRetry: () => fetchICOCoffeeReport()
        })
      }
    } catch (error) {
      setDialogState({
        ...dialogState,
        showModal: true,
        modalType: 'error',
        message: 'ICO_COFFEE_REPORT_DATA_FAILED',
        onHide: () => handleDismiss(),
        onRetry: () => fetchICOCoffeeReport()
      })
    } finally {
      setLoading(false)
      setShowTableMessage(false)
    }
  }

  const handleAddButtonClick = () => {
    history.push(ROUTE_STRINGS.addICOCoffeeReport)
  }

  const handleWindowResize = () => {
    setWindowSize(window.innerWidth)
  }

  const handleDismiss = () => {
    setDialogState({ ...dialogState, showModal: false })
  }

  const handleDeleteConfirmation = (rowData) => {
    setDialogState({
      ...dialogState,
      showModal: true,
      modalType: 'info',
      message: t('DELETE_COFFEE_REPORT'),
      onConfirmation: () => deleteICOReport(rowData),
      onHide: () => handleDismiss()
    })
  }

  const deleteICOReport = async ({ idReport }) => {
    setLoading(true)
    try {
      await apiAdapterCoffeeWeb.deleteICOCoffeeReport(idReport)
      setDialogState({
        ...dialogState,
        showModal: false
      })
      const updatedReport = icoReport.filter((el) => el.idReport !== idReport)

      setICOReport(updatedReport)
    } catch (error) {
      setDialogState({
        ...dialogState,
        showModal: true,
        modalType: 'error',
        message: 'DELETE_ICO_COFFEE_REPORT_FAILED',
        onHide: () => handleDismiss(),
        onRetry: () => deleteICOReport(idReport)
      })
    } finally {
      setLoading(false)
    }
  }

  const handleReportClick = (rowData) => {
    if (rowData?.isUpcoming) {
      history.push({ pathname: `${ROUTE_STRINGS.editICOCoffeeReport}/${rowData.idReport}`, state: rowData })
    }
  }

  const renderTableResponseMessage = () => <div>{showTableMessage ? t('FETCHING') : t('NO_RESULTS_FOUND')}</div>

  const renderActionColumn = (rowData) => (
    <div className="action-container">
      <Button type="button" icon="pi pi-trash" className="pi-delete-icon" tooltip={t('DELETE')} onClick={() => handleDeleteConfirmation(rowData)} tooltipOptions={{ position: 'top' }} data-testid="delete-button" />
    </div>
  )

  const renderUpcomingColumn = (rowData) => (
    <div className={`${rowData?.isUpcoming ? 'upcoming' : 'published'}`} onClick={() => handleReportClick(rowData)}>
      {rowData?.isUpcoming ? 'Upcoming' : 'Published'}
    </div>
  )

  const columns = [
    { body: renderUpcomingColumn, header: t('IS_UPCOMING'), className: 'upcoming-column' },
    { field: 'monthYear', header: t('YEAR_MONTH'), className: 'yearMonth' },
    { field: 'tags', header: t('TAG'), className: 'tag-column' },
    { field: 'title', header: t('TITLE'), className: 'title-column' },
    { field: 'reportLink', header: t('REPORT_LINK'), className: 'report-link-column' },
    { field: 'publishedDate', header: t('PUBLISH_DATE'), className: 'publish-date-column' },
    { body: renderActionColumn, header: t('ACTIONS'), className: 'ico-actions-column' }
  ]

  return (
    <div className="ico-report-table">
      <LayoutContainer title={'ICO_COFFEE_REPORT'} breadCrumbsTitle={'icoCoffeeReport'} showAddButton={true} handleAddButtonClick={handleAddButtonClick}>
        <DataTable value={icoReport} paginator={icoReport?.length > 0} rows={5} pageLinkSize={paginationButtons} emptyMessage={renderTableResponseMessage} className="ico-columns-container">
          {columns.map(({ field, header, body, className }) => (
            <Column key={field} field={field} header={header} body={body} className={className} />
          ))}
        </DataTable>

        <DialogModal {...dialogState} />
      </LayoutContainer>
    </div>
  )
}

export default Loader(ListICOCoffeeReport)
